import { useMemo } from "react";

import { useSourcingSettings } from "@components/SourcingThemes/hooks/useSourcingSettings";
import {
  ICompanyTag,
  IDealTag,
  IDocumentTag,
  IFolderTag,
  IThreadTag,
  IMeetingTag,
  INoteTag,
  IPeopleTag,
  IMemberTag,
  Tag,
  IThemeTag,
  IFundTag,
  IOpportunityTag,
  OptionsForTags,
  OptionsForTagsCopy,
} from "@interfaces/tag.interface";
import { SuggestionCompany } from "./SuggestionCompany";
import { SuggestionDeal } from "./SuggestionDeal";
import { SuggestionDocument } from "./SuggestionDocument";
import { SuggestionThread } from "./SuggestionThread";
import { SuggestionFolder } from "./SuggestionFolder";
import { SuggestionFund } from "./SuggestionFund";
import { SuggestionMeeting } from "./SuggestionMeeting";
import { SuggestionMember } from "./SuggestionMember";
import { SuggestionNote } from "./SuggestionNote";
import { SuggestionOpportunity } from "./SuggestionOpportunity";
import { SuggestionPeople } from "./SuggestionPeople";
import { SuggestionTheme } from "./SuggestionTheme";

type Props = {
  item: Tag;
  group: keyof OptionsForTags | keyof OptionsForTagsCopy;
} & React.HTMLAttributes<HTMLElement>;

export const SuggestionItem = ({ item, group, ...rest }: Props) => {
  const { data: sourcingSettings } = useSourcingSettings();

  const hasSingleZeroLevelTheme = sourcingSettings?.zero_level_count === 1;

  const renderSuggestions = useMemo(() => {
    switch (group) {
      case "meetings":
        return <SuggestionMeeting item={item as IMeetingTag} {...rest} />;
      case "deals":
        return <SuggestionDeal item={item as IDealTag} {...rest} />;
      case "companies":
        return <SuggestionCompany item={item as ICompanyTag} {...rest} />;
      case "ai_notes":
        return <SuggestionNote item={item as INoteTag} {...rest} />;
      case "notes":
        return <SuggestionNote item={item as INoteTag} {...rest} />;
      case "themes":
        return (
          <SuggestionTheme
            item={item as IThemeTag}
            isLevelOneBased={hasSingleZeroLevelTheme}
            {...rest}
          />
        );
      case "folders":
        return <SuggestionFolder item={item as IFolderTag} {...rest} />;
      case "funds":
        return <SuggestionFund item={item as IFundTag} {...rest} />;
      case "opportunities":
        return <SuggestionOpportunity item={item as IOpportunityTag} {...rest} />;
      case "people":
        return <SuggestionPeople showEmail item={item as IPeopleTag} {...rest} />;
      case "users":
        return <SuggestionMember showEmail item={item as IMemberTag} {...rest} />;
      case "members":
        return <SuggestionMember showEmail item={item as IMemberTag} {...rest} />;
      case "documents":
        return <SuggestionDocument item={item as IDocumentTag} {...rest} />;
      case "threads":
        return <SuggestionThread item={item as IThreadTag} {...rest} />;
    }
  }, [group, item]);

  return <>{renderSuggestions}</>;
};
